import { useCallback, useEffect, useState } from "react";

interface UseIntersectionObserverOptions {
  threshold?: number | number[];
  rootMargin?: string;
  triggerOnce?: boolean;
}

export function useIntersectionObserver(options: UseIntersectionObserverOptions = {}) {
  const { threshold = 0, rootMargin = "0px", triggerOnce = false } = options;

  const [node, setNode] = useState<HTMLDivElement | null>(null);
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasTriggered, setHasTriggered] = useState(false);

  // Stable callback ref: assign this to your element
  const targetRef = useCallback((el: HTMLDivElement | null) => {
    setNode(el);
  }, []);

  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      // Optional: fallback
      setIsIntersecting(true);
      return;
    }
    if (triggerOnce && hasTriggered) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const seen = entry.isIntersecting;
        setIsIntersecting(seen);
        if (seen && triggerOnce) {
          setHasTriggered(true);
          observer.unobserve(entry.target); // stop immediately when triggered once
          observer.disconnect();
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(node);

    return () => {
      observer.unobserve(node);
      observer.disconnect();
    };
  }, [node, threshold, rootMargin, triggerOnce, hasTriggered]);

  return { targetRef, isIntersecting };
}
